import api from '../api/api';
import { captureBadgeActor, isBadgeActorCurrent } from '../features/badges/progressEvents';

const postpopHosts = new Set(['postpop.kr', 'sandbox.postpop.kr', 'postpop-sandbox.kro.kr']);
export const buildAbsoluteUrl = (url, origin = window.location.origin) => {
  const parsed = new URL(url || window.location.href, origin);
  if (!['http:', 'https:'].includes(parsed.protocol) || parsed.username || parsed.password)
    throw new Error('Unsupported share URL');
  // Callers may hold a production canonical URL; its IDs still belong to the
  // current environment. Keep path/query/fragment intact for web destinations.
  return postpopHosts.has(parsed.hostname)
    ? `${origin}${parsed.pathname}${parsed.search}${parsed.hash}`
    : parsed.href;
};

// Build60: readable letters only, never turn escaped separators/control codes
// into message boundaries. Keep malformed escapes intact.
export const readablePostpopShareUrl = (url) => {
  return url.replace(/(?:%[c-f][0-9a-f](?:%[89ab][0-9a-f])+)+/gi, (encoded) => {
    try {
      const decoded = decodeURIComponent(encoded);
      return /^[\p{L}\p{M}\p{N}]+$/u.test(decoded) ? decoded : encoded;
    } catch {
      return encoded;
    }
  });
};

const readToken = () => {
  try {
    return localStorage.getItem('POSTPOP_AUTH_TOKEN');
  } catch {
    return null;
  }
};

const normalizeCopy = (text) => {
  const copy = text?.trim().replace(/\s+/g, ' ');
  return copy?.length > 180 ? `${copy.slice(0, 179).trimEnd()}…` : copy;
};

export const shareOrCopyUrl = async ({ url, title, text, usePageMetadata = false, event } = {}) => {
  const actor = captureBadgeActor();
  const token = readToken();
  const isSessionCurrent = () => !!token && isBadgeActorCurrent(actor) && readToken() === token;
  // Capture the target before opening an asynchronous system sheet.
  const sharedEvent = event && {
    ...event,
    eventType: 'CONTENT_SHARE',
    context: event.context ?? 'web_share',
  };
  const complete = (result) => {
    if (sharedEvent && isSessionCurrent()) {
      // Telemetry must not delay or undo a successful share. Check the captured
      // session again inside the interceptor before credentials are attached.
      void api
        .postAuth('/search/discovery/events', sharedEvent, {
          isSessionCurrent,
          timeout: 15000,
          suppressGlobalError: true,
        })
        .catch(() => undefined);
    }
    return result;
  };
  const copy = usePageMetadata ? '' : normalizeCopy(text);
  const shareUrl = readablePostpopShareUrl(buildAbsoluteUrl(url));
  const message = [copy, shareUrl].filter(Boolean).join('\n');
  const shareData = usePageMetadata
    ? { url: shareUrl }
    : {
        title: title || document.title,
        text: message,
      };

  if (navigator.share) {
    try {
      await navigator.share(shareData);
      return complete('shared');
    } catch (err) {
      if (err?.name === 'AbortError') {
        return 'cancelled';
      }
    }
  }

  // Both paths use one message with no trailing line; receiver formatting is
  // outside the Web Share API and must not trigger a second clipboard write.
  await navigator.clipboard.writeText(message);
  return complete('copied');
};
